/**
 * The query keys every issue-changing mutation must invalidate.
 *
 * WHY THIS EXISTS
 * The issues page renders two things off the SAME data through two different
 * caches: the table (`useIssues`, key ['issues', params]) and the four chart
 * cards (`useProjectSummary`, key ['project-summary', projectId]). Until
 * 2026-08-06 every mutation invalidated ['issues'] alone, so after adding
 * issues the table filled in while Severity Breakdown, Status, Issue Types and
 * Issue Age still read "No issues yet". TanStack matches invalidation by key
 * PREFIX, so ['project-summary', id] never matched, and useProjectSummary's
 * staleTime of 60_000 stopped it refetching on its own for a full minute.
 *
 * It was 12 call sites (useIssues create/update/delete, seven useBulkActions
 * mutations, and AddScanIssuesModal), each repeating [['issues']]. Fixing them
 * individually would have left the 13th to forget again, which is how this
 * drifted in the first place: `useNotifications.ts` already invalidated all
 * three keys correctly, and nothing kept the others in step with it.
 *
 * Add a key here and every issue mutation picks it up.
 */

/** The issue list itself (`useIssues`). */
export const ISSUES_KEY = ['issues'] as const;

/** Feeds the four chart cards on a project's issues page. */
export const PROJECT_SUMMARY_KEY = ['project-summary'] as const;

/** Feeds the same counts on /dashboard. */
export const DASHBOARD_SUMMARY_KEY = ['dashboard-summary'] as const;

/**
 * Pass to `useApiMutation({ invalidateKeys })`, or iterate for a manual
 * `queryClient.invalidateQueries` call. Prefix keys on purpose: ['issues']
 * clears every paged/filtered variant, ['project-summary'] every project's.
 */
export const ISSUE_MUTATION_INVALIDATE_KEYS: readonly (readonly unknown[])[] = [
  ISSUES_KEY,
  PROJECT_SUMMARY_KEY,
  DASHBOARD_SUMMARY_KEY,
];
